import cogoToast from 'cogo-toast';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../helpers/axiosConfig';

// Helper function to check auth and handle redirects
const checkAuthentication = () => {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    cogoToast.error('Please login to manage favorites', {
      position: 'bottom-left',
    });
    // We'll handle the redirect in the component
    return false;
  }
  return true;
};

// Async thunks for API calls
export const fetchFavorites = createAsyncThunk(
  'wishlist/fetchFavorites',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/favorite');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        // Clear any stored tokens on 401
        localStorage.removeItem('auth_token');
      }
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch favorites'
      );
    }
  }
);

export const addFavorite = createAsyncThunk(
  'wishlist/addFavorite',
  async (product, { rejectWithValue, dispatch }) => {
    if (!checkAuthentication()) {
      return rejectWithValue('authentication_required');
    }

    // Optimistically update the UI
    dispatch(setOptimisticFavorite({ id: product.id, status: true }));

    try {
      const response = await api.post(`/addfavorite/${product.id}`);
      return { product, response: response.data };
    } catch (error) {
      // Revert the optimistic update
      dispatch(setOptimisticFavorite({ id: product.id, status: false }));

      if (error.response?.status === 401) {
        localStorage.removeItem('auth_token');
        return rejectWithValue('authentication_required');
      }
      return rejectWithValue(
        error.response?.data?.message || 'Failed to add to favorites'
      );
    }
  }
);

export const removeFavorite = createAsyncThunk(
  'wishlist/removeFavorite',
  async (productId, { rejectWithValue, dispatch, getState }) => {
    if (!checkAuthentication()) {
      return rejectWithValue('authentication_required');
    }

    // Find the product in wishlist
    const state = getState();
    const product = state.wishlist.wishlistItems.find(
      (item) => item.id === productId
    );

    // Optimistically update the UI
    dispatch(setOptimisticFavorite({ id: productId, status: false }));

    try {
      const response = await api.delete(`/remove/favorite/${productId}`);
      return { id: productId, response: response.data };
    } catch (error) {
      // Revert the optimistic update
      if (product) {
        dispatch(setOptimisticFavorite({ id: productId, status: true }));
      }

      if (error.response?.status === 401) {
        localStorage.removeItem('auth_token');
        return rejectWithValue('authentication_required');
      }
      return rejectWithValue(
        error.response?.data?.message || 'Failed to remove from favorites'
      );
    }
  }
);

export const clearFavorites = createAsyncThunk(
  'wishlist/clearFavorites',
  async (_, { rejectWithValue }) => {
    if (!checkAuthentication()) {
      return rejectWithValue('authentication_required');
    }

    try {
      const response = await api.delete('/clear/favorites');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('auth_token');
        return rejectWithValue('authentication_required');
      }
      return rejectWithValue(
        error.response?.data?.message || 'Failed to clear favorites'
      );
    }
  }
);

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState: {
    wishlistItems: [],
    loading: false,
    addRemoveLoading: {}, // Track loading state by product ID
    error: null,
  },
  reducers: {
    // Keep these for compatibility with existing code but they'll now just trigger notifications
    addToWishlist(state, action) {
      // This is now handled by the async thunk
      cogoToast.info('Processing...', { position: 'bottom-left' });
    },
    deleteFromWishlist(state, action) {
      // This is now handled by the async thunk
      cogoToast.info('Processing...', { position: 'bottom-left' });
    },
    deleteAllFromWishlist(state) {
      // This is now handled by the async thunk
      cogoToast.info('Processing...', { position: 'bottom-left' });
    },
    resetWishlistError(state) {
      state.error = null;
    },
    setOptimisticFavorite(state, action) {
      const { id, status } = action.payload;

      // Ensure addRemoveLoading is initialized
      if (!state.addRemoveLoading) {
        state.addRemoveLoading = {};
      }

      if (status) {
        // If adding to favorites
        if (!state.wishlistItems.some((item) => item.id === id)) {
          // If product not already in wishlist, add a placeholder
          state.wishlistItems.push({ id, isPlaceholder: true });
        }
      } else {
        // If removing from favorites
        state.wishlistItems = state.wishlistItems.filter(
          (item) => item.id !== id
        );
      }

      // Set loading state for this specific product
      state.addRemoveLoading[id] = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch favorites
      .addCase(fetchFavorites.pending, (state) => {
        state.loading = true;
        // Ensure addRemoveLoading is initialized
        if (!state.addRemoveLoading) {
          state.addRemoveLoading = {};
        }
      })
      .addCase(fetchFavorites.fulfilled, (state, action) => {
        state.loading = false;
        // Handle the correct structure from the API
        if (action.payload && action.payload.favorite) {
          // Extract products from favorite items
          state.wishlistItems = action.payload.favorite.map(
            (item) => item.product
          );

          // Mark all loaded products as favorite for correct UI state
          if (state.wishlistItems.length > 0) {
            state.wishlistItems.forEach((product) => {
              if (product && product.id) {
                product.is_favorite = true;
              }
            });
          }
        } else {
          state.wishlistItems = [];
        }
      })
      .addCase(fetchFavorites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add to favorites
      .addCase(addFavorite.pending, (state, action) => {
        const productId = action.meta.arg.id;
        // Ensure addRemoveLoading is initialized
        if (!state.addRemoveLoading) {
          state.addRemoveLoading = {};
        }
        state.addRemoveLoading[productId] = true;
      })
      .addCase(addFavorite.fulfilled, (state, action) => {
        const productId = action.payload.product.id;

        // Ensure addRemoveLoading is initialized
        if (!state.addRemoveLoading) {
          state.addRemoveLoading = {};
        }

        // Remove any placeholder
        state.wishlistItems = state.wishlistItems.filter(
          (item) => !item.isPlaceholder
        );

        // Ensure the product is in the list with full details
        const isInWishlist = state.wishlistItems.findIndex(
          (item) => item.id === productId
        );
        if (isInWishlist === -1) {
          // Add the product with is_favorite flag set
          const productWithFavorite = {
            ...action.payload.product,
            is_favorite: true,
          };
          state.wishlistItems.push(productWithFavorite);
        } else {
          // Update existing product
          state.wishlistItems[isInWishlist].is_favorite = true;
        }

        // Clear loading state
        state.addRemoveLoading[productId] = false;

        cogoToast.success('Added to favorites', { position: 'bottom-left' });
      })
      .addCase(addFavorite.rejected, (state, action) => {
        // Ensure addRemoveLoading is initialized
        if (!state.addRemoveLoading) {
          state.addRemoveLoading = {};
        }

        // Clear any loading state
        if (action.meta?.arg?.id) {
          state.addRemoveLoading[action.meta.arg.id] = false;

          // Remove the placeholder product if it was added optimistically
          state.wishlistItems = state.wishlistItems.filter(
            (item) => !(item.isPlaceholder && item.id === action.meta.arg.id)
          );
        }

        state.error = action.payload;
        if (action.payload === 'authentication_required') {
          // Handle redirect in component
        } else {
          cogoToast.error(action.payload || 'Failed to add to favorites', {
            position: 'bottom-left',
          });
        }
      })

      // Remove from favorites
      .addCase(removeFavorite.pending, (state, action) => {
        const productId = action.meta.arg;
        // Ensure addRemoveLoading is initialized
        if (!state.addRemoveLoading) {
          state.addRemoveLoading = {};
        }
        state.addRemoveLoading[productId] = true;
      })
      .addCase(removeFavorite.fulfilled, (state, action) => {
        const productId = action.payload.id;

        // Ensure addRemoveLoading is initialized
        if (!state.addRemoveLoading) {
          state.addRemoveLoading = {};
        }

        // Already removed optimistically, just clear loading state
        state.addRemoveLoading[productId] = false;

        cogoToast.error('Removed from favorites', { position: 'bottom-left' });
      })
      .addCase(removeFavorite.rejected, (state, action) => {
        // Ensure addRemoveLoading is initialized
        if (!state.addRemoveLoading) {
          state.addRemoveLoading = {};
        }

        // Clear any loading state
        if (action.meta?.arg) {
          const productId = action.meta.arg;
          state.addRemoveLoading[productId] = false;

          // If removal failed and we have the product, add it back
          const product = action.meta.state?.wishlist.wishlistItems.find(
            (item) => item.id === productId
          );

          if (product) {
            state.wishlistItems.push({ ...product, is_favorite: true });
          }
        }

        state.error = action.payload;
        if (action.payload === 'authentication_required') {
          // Handle redirect in component
        } else {
          cogoToast.error(action.payload || 'Failed to remove from favorites', {
            position: 'bottom-left',
          });
        }
      })

      // Clear favorites
      .addCase(clearFavorites.pending, (state) => {
        state.loading = true;
        // Ensure addRemoveLoading is initialized
        if (!state.addRemoveLoading) {
          state.addRemoveLoading = {};
        }
      })
      .addCase(clearFavorites.fulfilled, (state) => {
        state.loading = false;
        state.wishlistItems = [];
        state.addRemoveLoading = {};
        cogoToast.error('All favorites cleared', { position: 'bottom-left' });
      })
      .addCase(clearFavorites.rejected, (state, action) => {
        state.loading = false;
        // Ensure addRemoveLoading is initialized
        if (!state.addRemoveLoading) {
          state.addRemoveLoading = {};
        }
        state.error = action.payload;
        if (action.payload === 'authentication_required') {
          // Handle redirect in component
        } else {
          cogoToast.error(action.payload || 'Failed to clear favorites', {
            position: 'bottom-left',
          });
        }
      });
  },
});

export const {
  addToWishlist,
  deleteFromWishlist,
  deleteAllFromWishlist,
  resetWishlistError,
  setOptimisticFavorite,
} = wishlistSlice.actions;
export default wishlistSlice.reducer;
